import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { DayView } from '../views/DayView';
import { toDateKey, toMonthKey, toWeekKey } from '../../lib/datetime';
import { setListPath, useListPath } from './listPath';
import { SyncIndicator } from '../components/SyncIndicator';

/**
 * 週ビューの7列と月ビューのカードグリッドは幅を必要とするため、
 * タブレット/PCでも全幅で表示する。分割ペインは日ビューと詳細に適用する（仕様書 §12.1・§12.5）。
 */
const FULL_WIDTH_PREFIXES = [
  '/week/',
  '/month/',
  '/search',
  '/settings',
  '/goals',
  '/reports',
  '/projects',
];

interface NavItem {
  to: string;
  match: string;
  label: string;
  icon: string;
  /** スマホの下部タブに常時出すか。出さないものはメニューへ入れる。 */
  primary: boolean;
}

function navItems(): NavItem[] {
  const today = toDateKey(new Date());
  return [
    { to: `/day/${today}`, match: '/day', label: '日', icon: '☀', primary: true },
    { to: `/week/${toWeekKey(new Date())}`, match: '/week', label: '週', icon: '▤', primary: true },
    { to: `/month/${toMonthKey(new Date())}`, match: '/month', label: '月', icon: '▦', primary: true },
    { to: '/projects', match: '/projects', label: 'プロジェクト', icon: '◆', primary: false },
    { to: '/goals', match: '/goals', label: 'ゴール', icon: '◎', primary: false },
    { to: '/reports', match: '/reports', label: 'レポート', icon: '▥', primary: false },
    { to: '/search', match: '/search', label: '検索', icon: '⌕', primary: true },
    { to: '/settings', match: '/settings', label: '設定', icon: '⚙', primary: false },
  ];
}

function NavLinks({ items, className }: { items: NavItem[]; className: string }) {
  const { pathname } = useLocation();
  return (
    <nav className={className}>
      {items.map((it) => (
        <NavLink
          key={it.match}
          to={it.to}
          className={pathname.startsWith(it.match) ? 'nav-item is-active' : 'nav-item'}
        >
          <span className="nav-icon" aria-hidden>
            {it.icon}
          </span>
          <span className="nav-label">{it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** スマホ用。主要4つをタブに出し、残りはメニューへ入れる。 */
function PhoneNav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const items = navItems();
  const primary = items.filter((i) => i.primary);
  const rest = items.filter((i) => !i.primary);
  const restActive = rest.some((i) => pathname.startsWith(i.match));

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            {rest.map((it) => (
              <NavLink key={it.match} to={it.to} className="sheet-item">
                <span className="nav-icon" aria-hidden>
                  {it.icon}
                </span>
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <nav className="nav nav-bottom">
        {primary.map((it) => (
          <NavLink
            key={it.match}
            to={it.to}
            className={pathname.startsWith(it.match) ? 'nav-item is-active' : 'nav-item'}
          >
            <span className="nav-icon" aria-hidden>
              {it.icon}
            </span>
            <span className="nav-label">{it.label}</span>
          </NavLink>
        ))}
        <button
          className={`nav-item${restActive || open ? ' is-active' : ''}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="nav-icon" aria-hidden>
            ⋯
          </span>
          <span className="nav-label">メニュー</span>
        </button>
      </nav>
    </>
  );
}

export function AppShell() {
  const bp = useBreakpoint();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const listPath = useListPath();
  useEffect(() => {
    if (pathname.startsWith('/day/')) setListPath(pathname);
  }, [pathname]);

  // PC のキーボードショートカット（仕様書 §12.8。フェーズ1では最小限）
  useEffect(() => {
    if (bp !== 'desktop') return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const map: Record<string, string> = {
        n: '/new',
        '/': '/search',
        '1': `/day/${toDateKey(new Date())}`,
        '2': `/week/${toWeekKey(new Date())}`,
        '3': `/month/${toMonthKey(new Date())}`,
        g: '/goals',
        p: '/projects',
        r: '/reports',
        t: `/day/${toDateKey(new Date())}`,
      };
      const to = map[e.key];
      if (to) {
        e.preventDefault();
        navigate(to);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bp, navigate]);

  const fullWidth = FULL_WIDTH_PREFIXES.some((p) => pathname.startsWith(p));
  const split = bp !== 'phone' && !fullWidth;
  const showDetail = split && !pathname.startsWith('/day/');
  // 編集中はFABを出さない（フォームに重なるうえ、既に作成中のため意味がない）
  const isEditor = pathname === '/new' || pathname.endsWith('/edit');

  return (
    <div className={`shell shell-${bp}`}>
      {bp === 'desktop' && (
        <aside className="rail">
          <div className="brand-row">
            <span className="brand">Journal</span>
            <SyncIndicator compact />
          </div>
          <NavLinks items={navItems()} className="nav nav-rail" />
        </aside>
      )}

      {bp === 'tablet' && (
        <header className="topbar">
          <span className="brand">Journal</span>
          <NavLinks items={navItems()} className="nav nav-top" />
          <SyncIndicator compact />
          <Link to="/new" className="primary-btn new-btn">
            ＋ 新規
          </Link>
        </header>
      )}

      <main className="main">
        {split ? (
          <div className="split">
            <div className="pane pane-list">
              <DayView dateKey={listPath.slice('/day/'.length)} />
            </div>
            <div className="pane pane-detail">
              {showDetail ? (
                <Outlet />
              ) : (
                <div className="empty placeholder">
                  <p>左の一覧から記録を選んでください</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {bp === 'phone' && !isEditor && (
        <div className="sync-float">
          <SyncIndicator compact />
        </div>
      )}

      {bp === 'phone' && (
        <>
          {!isEditor && (
            <Link to="/new" className="fab" aria-label="新規作成">
              ＋
            </Link>
          )}
          <PhoneNav />
        </>
      )}
    </div>
  );
}
